import { type DragEvent, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ChevronRight, GripVertical, Layers, Plus } from 'lucide-react'
import { useCourse, useCourseModules, useReorderModules } from '@/hooks/useCourses'
import { CourseModuleModel } from '@/models/course.model'
import { paths } from '@/routes/path'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

function formatDateLabel(value?: string) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function CourseModulesPage() {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()

  const { data: course, isLoading: isCourseLoading, error: loadCourseError } = useCourse(courseId)
  const { data: modules = [], isLoading: isModulesLoading } = useCourseModules(courseId)
  const reorderModulesMutation = useReorderModules(courseId)

  const [orderedModules, setOrderedModules] = useState<CourseModuleModel[]>([])
  const [draggedModuleId, setDraggedModuleId] = useState<string | null>(null)
  const [dropTargetModuleId, setDropTargetModuleId] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [courseError, setCourseError] = useState<string | null>(null)

  const sortedModules = useMemo(
    () =>
      modules
        .slice()
        .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)),
    [modules],
  )

  useEffect(() => {
    setOrderedModules(sortedModules)
  }, [sortedModules])

  const moduleResourceUsage = useMemo(() => {
    const usage = new Map<string, number>()
    if (course?.resources) {
      course.resources.forEach((resource) => {
        if (!resource.moduleId) return
        usage.set(resource.moduleId, (usage.get(resource.moduleId) ?? 0) + 1)
      })
    }
    return usage
  }, [course])

  useEffect(() => {
    if (loadCourseError) {
      setCourseError((loadCourseError as Error).message || 'Unable to load course details.')
    } else {
      setCourseError(null)
    }
  }, [loadCourseError])

  if (!courseId) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-secondary-500">
        Course identifier is required.
      </div>
    )
  }

  if (isCourseLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center text-secondary-500">
        Loading course information…
      </div>
    )
  }

  if (!course) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-secondary-500">
        <p className="text-lg font-semibold">Course not found</p>
        <Button variant="link" className="text-primary-600 hover:text-primary-700" onClick={() => navigate(-1)}>
          Go back
        </Button>
      </div>
    )
  }

  const navigateToModuleCreate = () => {
    navigate(paths.app.courses.modules.create.build(course.id))
  }

  const navigateToModuleDetail = (moduleId: string) => {
    navigate(paths.app.courses.modules.detail.build(course.id, moduleId))
  }

  const applyModuleReorder = (sourceId: string, targetId: string, position: 'before' | 'after') => {
    setOrderedModules((prev) => {
      const previousState = [...prev]
      const updated = [...prev]
      if (sourceId === targetId) {
        return prev
      }

      const fromIndex = updated.findIndex((module) => module.id === sourceId)
      if (fromIndex === -1) {
        return prev
      }

      const [movedModule] = updated.splice(fromIndex, 1)

      let insertionIndex = updated.findIndex((module) => module.id === targetId)
      if (insertionIndex === -1) {
        return prev
      }
      if (position === 'after') {
        insertionIndex += 1
      }

      updated.splice(insertionIndex, 0, movedModule)

      const payload = updated.map((module, index) => ({
        id: module.id,
        title: module.title,
        description: module.description,
        isActive: module.isActive,
        order: index + 1,
      }))

      reorderModulesMutation.mutate(payload, {
        onError: () => {
          setOrderedModules(previousState)
        },
      })

      return updated
    })
  }

  const handleModuleDragStart = (event: DragEvent<HTMLButtonElement>, moduleId: string) => {
    if (reorderModulesMutation.isPending) {
      event.preventDefault()
      return
    }
    event.stopPropagation()
    setDraggedModuleId(moduleId)
    setIsDragging(true)
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', moduleId)
  }

  const handleModuleDragEnter = (event: DragEvent<HTMLTableRowElement>, moduleId: string) => {
    event.preventDefault()
    if (draggedModuleId && draggedModuleId !== moduleId) {
      setDropTargetModuleId(moduleId)
    }
  }

  const handleModuleDragOver = (event: DragEvent<HTMLTableRowElement>) => {
    event.preventDefault()
  }

  const handleModuleDrop = (event: DragEvent<HTMLTableRowElement>, moduleId: string) => {
    event.preventDefault()
    const sourceId = event.dataTransfer.getData('text/plain') || draggedModuleId
    setDropTargetModuleId(null)
    if (!sourceId || sourceId === moduleId) {
      setIsDragging(false)
      return
    }

    const { top, height } = event.currentTarget.getBoundingClientRect()
    const position: 'before' | 'after' = event.clientY > top + height / 2 ? 'after' : 'before'

    applyModuleReorder(sourceId, moduleId, position)
    setIsDragging(false)
  }

  const handleModuleDragEnd = () => {
    setDraggedModuleId(null)
    setDropTargetModuleId(null)
    setIsDragging(false)
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm text-secondary-500">
            <Link
              to={paths.app.courses.root.absolute}
              className="inline-flex items-center gap-2 hover:text-secondary-700"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to courses
            </Link>
            <ChevronRight className="h-4 w-4" />
            <Link
              to={paths.app.courses.manage.build(course.id)}
              className="inline-flex items-center gap-1 text-secondary-500 hover:text-secondary-700"
            >
              <Layers className="h-4 w-4" />
              Manage Course
            </Link>
          </div>
          <h1 className="text-3xl font-bold text-secondary-900">Modules · {course.title}</h1>
          <p className="text-secondary-500">Organise modules, manage their resources, and control the learning journey.</p>
        </div>
        <Button
          onClick={navigateToModuleCreate}
          className="bg-primary-600 text-white hover:bg-primary-700"
        >
          <Plus className="mr-2 h-4 w-4" />
          New Module
        </Button>
      </div>

      {courseError && (
        <div className="rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-600">
          {courseError}
        </div>
      )}

      <Card className="border-0 shadow-md">
        <CardHeader>
          <CardTitle>Course Modules</CardTitle>
        </CardHeader>
        <CardContent>
          {reorderModulesMutation.isError && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
              {reorderModulesMutation.error instanceof Error
                ? reorderModulesMutation.error.message
                : 'Unable to reorder modules.'}
            </div>
          )}
          {reorderModulesMutation.isPending && (
            <div className="mb-4 text-sm text-secondary-500">Saving module order…</div>
          )}
          {isModulesLoading ? (
            <div className="py-6 text-center text-secondary-500">Loading modules…</div>
          ) : sortedModules.length === 0 ? (
            <div className="rounded-md border border-secondary-200 bg-secondary-50 px-4 py-6 text-center text-secondary-500">
              No modules have been added yet.
            </div>
          ) : (
            <div className="overflow-hidden rounded-md border border-secondary-200">
              <div className="w-full overflow-x-auto">
                <table className="min-w-full divide-y divide-secondary-200">
                  <thead className="bg-secondary-50 text-left text-xs font-semibold uppercase tracking-wide text-secondary-500">
                    <tr>
                      <th className="px-4 py-3"></th>
                      <th className="px-4 py-3">Module</th>
                      <th className="px-4 py-3">Resource</th>
                      <th className="px-4 py-3">Last Updated</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100 bg-white">
                    {orderedModules.map((module, index) => {
                      const resourceCount = moduleResourceUsage.get(module.id) ?? 0
                      const hasResource = resourceCount > 0
                      const displayOrder = index + 1
                      const isDropTarget = dropTargetModuleId === module.id
                      const isDragged = draggedModuleId === module.id

                      return (
                        <tr
                          key={module.id}
                          tabIndex={0}
                          onClick={() => {
                            if (isDragging || reorderModulesMutation.isPending) return
                            navigateToModuleDetail(module.id)
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              if (isDragging || reorderModulesMutation.isPending) return
                              navigateToModuleDetail(module.id)
                            }
                          }}
                          onDragEnter={(event) => handleModuleDragEnter(event, module.id)}
                          onDragOver={handleModuleDragOver}
                          onDrop={(event) => handleModuleDrop(event, module.id)}
                          className={`cursor-pointer transition-colors hover:bg-secondary-50 focus:outline-none focus-visible:bg-secondary-100 ${
                            isDropTarget || isDragged ? 'bg-primary-50' : ''
                          } ${reorderModulesMutation.isPending ? 'opacity-80' : ''}`}
                        >
                          <td className="px-4 py-4 text-sm font-medium text-secondary-900">
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                className="flex h-8 w-8 cursor-grab items-center justify-center rounded text-secondary-400 hover:text-secondary-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200 active:cursor-grabbing"
                                draggable={!reorderModulesMutation.isPending}
                                onClick={(event) => event.stopPropagation()}
                                onDragStart={(event) => handleModuleDragStart(event, module.id)}
                                onDragEnd={handleModuleDragEnd}
                                aria-label="Reorder module"
                              >
                                <GripVertical className="h-4 w-4" />
                              </button>
                              <span className="text-secondary-500">{displayOrder}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="space-y-1">
                              <p className="text-sm font-semibold text-secondary-900">{module.title}</p>
                              {module.description && (
                                <p className="text-sm text-secondary-500 line-clamp-2">{module.description}</p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {hasResource ? (
                              <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">
                                {resourceCount} linked {resourceCount === 1 ? 'resource' : 'resources'}
                              </Badge>
                            ) : (
                              <span className="text-sm text-secondary-400">No resources</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-secondary-500">
                            {formatDateLabel(module.updatedAt ?? module.createdAt)}
                          </td>
                          <td className="px-4 py-4 text-right text-sm text-primary-600 underline-offset-4 hover:underline">
                            View details
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
